import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { columns } from './columns'
import { DataTable } from './data-table'
import { useAdSupplierGroups } from '@/hooks/useAdSupplierGroups'
import { useViewMode } from '@/stores/viewMode'
import { PageHeader } from '@/components/PageHeader'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AdSupplierGroupDialog } from './AdSupplierGroupDialog'
import { AdSupplierGroup } from '@magnetar/admin-api-interfaces'
import { adSupplierGroupService } from '@/services/ad_supplier_group_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

export default function AdSupplierGroupsPage() {
  const { token } = useAuth()
  const { mode, id: providerId } = useViewMode()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<AdSupplierGroup | undefined>(undefined)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingGroup, setDeletingGroup] = useState<AdSupplierGroup | null>(null)

  const { adSupplierGroups, pagination, isLoading, isError, mutate } = useAdSupplierGroups({
    page,
    pageSize,
    provider_id: providerId || undefined,
  })

  // Filter by search
  const filteredGroups = adSupplierGroups?.filter(
    (group) =>
      group.group_name.toLowerCase().includes(search.toLowerCase()) ||
      (group.group_code && group.group_code.toLowerCase().includes(search.toLowerCase())),
  )

  const handleEdit = (group: AdSupplierGroup) => {
    setEditingGroup(group)
    setDialogOpen(true)
  }

  const handleDelete = (group: AdSupplierGroup) => {
    setDeletingGroup(group)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!token || !deletingGroup) return

    const result = await adSupplierGroupService.deleteAdSupplierGroup(
      deletingGroup.ad_supplier_group_id,
      token,
    )

    if (result.success) {
      toast.success('刪除廣告商集團成功')
      mutate()
    } else {
      toast.error('刪除廣告商集團失敗')
    }

    setDeleteDialogOpen(false)
    setDeletingGroup(null)
  }

  const handleDialogSuccess = () => {
    mutate()
    setEditingGroup(undefined)
  }

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setEditingGroup(undefined)
    }
  }

  // Generate page numbers for pagination
  const generatePaginationItems = () => {
    if (!pagination) return []

    const items = []
    const { page: currentPage, totalPages } = pagination

    // Always show first page
    items.push(1)

    if (currentPage > 3) {
      items.push('ellipsis')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) {
      items.push('ellipsis')
    }

    // Always show last page
    if (totalPages > 1) {
      items.push(totalPages)
    }

    return items
  }

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">載入廣告商集團列表失敗。請稍後再試。</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="廣告商集團管理"
        description="管理廣告商集團資訊"
        level={mode === 'admin' ? 'page' : 'section'}
      >
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          新增集團
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={filteredGroups || []}
            onSearch={setSearch}
            meta={{
              reloadData: () => mutate(),
              onEdit: handleEdit,
              onDelete: handleDelete,
            }}
          />

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page > 1) setPage(page - 1)
                      }}
                    />
                  </PaginationItem>

                  {generatePaginationItems().map((item, index) => {
                    if (item === 'ellipsis') {
                      return (
                        <PaginationItem key={`ellipsis-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )
                    }

                    return (
                      <PaginationItem key={item}>
                        <PaginationLink
                          href="#"
                          isActive={item === page}
                          onClick={(e) => {
                            e.preventDefault()
                            setPage(item as number)
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (pagination && page < pagination.totalPages) setPage(page + 1)
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      <AdSupplierGroupDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        onSuccess={handleDialogSuccess}
        adSupplierGroup={editingGroup}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作將刪除廣告商集團「{deletingGroup?.group_name}」，此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
